import { useEffect, useState } from 'react';
import {
  FRICTION_SUBTYPE_OPTIONS_BY_TYPE,
  frictionSubtypeLabel,
  INTERRUPTION_SOURCES,
  RESCUE_SCRIPT_DRAFTS_STORAGE_KEY,
  RESCUE_PREP_ITEMS,
  RESCUES
} from '../constants';
import type { FrictionSubtype, InterruptionSource, PingType, RescueKind, RescuePrepChecklist } from '../types';
import type { RecentPingContext } from '../lib/rescuePreferences';
import { BreathingBox } from './BreathingBox';

function rescueBadge(kind: RescueKind): string {
  if (kind === 'reset_2m') return '2M';
  if (kind === 'make_smaller_10m') return '10M';
  if (kind === 'focus_25m') return '25M';
  if (kind === 'breathe_90s') return 'BOX';
  if (kind === 'brain_dump_2m') return 'DUMP';
  if (kind === 'walk_5m') return 'WALK';
  if (kind === 'stand_water') return 'SIP';
  return '1S';
}

type RescuePickerProps = {
  scripts?: Record<RescueKind, string>;
  defaultSound?: boolean;
  favoriteRescues?: RescueKind[];
  onToggleFavorite?: (kind: RescueKind) => void;
  onSaveScript?: (kind: RescueKind, script: string) => void;
  pingType?: PingType | null;
  subtype?: FrictionSubtype | null;
  interruptionSource?: InterruptionSource | null;
  onSetSubtype?: (subtype: FrictionSubtype | null) => void;
  onSetInterruption?: (source: InterruptionSource | null) => void;
  interruptionLabel?: (source: InterruptionSource) => string;
  rescueKindLabel?: (kind: RescueKind) => string;
  rescueGuide: (kind: RescueKind) => {
    summary: string;
    prompt: string | null;
    actionLabel: string;
    breathing: {
      intro: string;
      inhale: string;
      hold: string;
      exhale: string;
    };
  };
  subtypeLabel?: (subtype: FrictionSubtype) => string;
  prepItemLabel?: (key: 'phoneSilenced' | 'tabsClosed' | 'docOpen' | 'timerVisible') => string;
  topPickKind?: RescueKind | null;
  recentContext?: RecentPingContext | null;
  labels?: {
    whatsHappening: string;
    pulledAway: string;
    none: string;
    chooseRescue: string;
    contextStep: string;
    contextHint: string;
    rescueStep: string;
    rescueHint: string;
    supportStep: string;
    supportHint: string;
    topPick: string;
    favorites: string;
    recentContextHint: string;
    applyLastContext: string;
    start: string;
    prepChecklist: string;
    resetTip: string;
    optionalDetails: string;
    smallestStepPlaceholder: string;
    start10mWithNote: string;
    sentencePlaceholder: string;
    saveSentence: string;
    focusSound: string;
    editRescueText: string;
    saveScript: string;
    savedSuffix: string;
    favoriteOn: string;
    favoriteOff: string;
  };
  onApplyRecentContext?: (context: RecentPingContext) => void;
  onStart: (kind: RescueKind, note: string | null, sound: boolean, prep: RescuePrepChecklist) => void;
};

export function RescuePicker({
  scripts,
  defaultSound,
  favoriteRescues,
  onToggleFavorite,
  onSaveScript,
  pingType,
  subtype,
  interruptionSource,
  onSetSubtype,
  onSetInterruption,
  interruptionLabel,
  rescueKindLabel,
  rescueGuide,
  subtypeLabel,
  prepItemLabel,
  topPickKind,
  recentContext,
  labels,
  onApplyRecentContext,
  onStart
}: RescuePickerProps) {
  const defaultScripts = RESCUES.reduce<Record<RescueKind, string>>((acc, rescue) => {
    acc[rescue.kind] = scripts?.[rescue.kind] ?? '';
    return acc;
  }, {} as Record<RescueKind, string>);
  const defaultDirty = RESCUES.reduce<Record<RescueKind, boolean>>((acc, rescue) => {
    acc[rescue.kind] = false;
    return acc;
  }, {} as Record<RescueKind, boolean>);
  const [smallerNote, setSmallerNote] = useState('');
  const [sentence, setSentence] = useState('');
  const [sound, setSound] = useState(defaultSound ?? false);
  const [scriptDrafts, setScriptDrafts] = useState<Record<RescueKind, string>>(defaultScripts);
  const [scriptDirty, setScriptDirty] = useState<Record<RescueKind, boolean>>(defaultDirty);
  const [prep, setPrep] = useState<RescuePrepChecklist>({
    phoneSilenced: false,
    tabsClosed: false,
    docOpen: false,
    timerVisible: false
  });
  const subtypeOptions = pingType ? FRICTION_SUBTYPE_OPTIONS_BY_TYPE[pingType] : [];
  const hasLegacySelectedSubtype = !!subtype && subtypeOptions.every((item) => item.subtype !== subtype);
  const l = {
    whatsHappening: labels?.whatsHappening ?? "What's happening?",
    pulledAway: labels?.pulledAway ?? 'What pulled you away?',
    none: labels?.none ?? 'None',
    chooseRescue: labels?.chooseRescue ?? 'Choose a rescue',
    contextStep: labels?.contextStep ?? 'Optional context',
    contextHint: labels?.contextHint ?? 'Only open this if you want a better suggestion.',
    rescueStep: labels?.rescueStep ?? 'Start one rescue',
    rescueHint: labels?.rescueHint ?? 'Pick the bit of support that feels easiest to start.',
    supportStep: labels?.supportStep ?? 'Optional setup',
    supportHint: labels?.supportHint ?? 'Only use this if it would make the rescue easier to start.',
    topPick: labels?.topPick ?? 'Top pick',
    favorites: labels?.favorites ?? 'Favorites',
    recentContextHint: labels?.recentContextHint ?? 'From a recent similar moment',
    applyLastContext: labels?.applyLastContext ?? 'Use last details',
    start: labels?.start ?? 'Start',
    prepChecklist: labels?.prepChecklist ?? 'Prep checklist (for timed rescues)',
    resetTip: labels?.resetTip ?? 'Two-minute reset: stand up, breathe slow, sip water if possible.',
    optionalDetails: labels?.optionalDetails ?? 'Optional details',
    smallestStepPlaceholder: labels?.smallestStepPlaceholder ?? "What's the smallest next step?",
    start10mWithNote: labels?.start10mWithNote ?? 'Start 10-min with note',
    sentencePlaceholder: labels?.sentencePlaceholder ?? 'Write 1 sentence',
    saveSentence: labels?.saveSentence ?? 'Save sentence',
    focusSound: labels?.focusSound ?? 'Use sound on focus timer end',
    editRescueText: labels?.editRescueText ?? 'Edit rescue text',
    saveScript: labels?.saveScript ?? 'Save script',
    savedSuffix: labels?.savedSuffix ?? '(saved)',
    favoriteOn: labels?.favoriteOn ?? 'Unfavorite',
    favoriteOff: labels?.favoriteOff ?? 'Favorite'
  };

  useEffect(() => {
    const raw = localStorage.getItem(RESCUE_SCRIPT_DRAFTS_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Record<RescueKind, string>;
      setScriptDrafts((prev) => ({ ...prev, ...parsed }));
    } catch {
      // ignore invalid local draft
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(RESCUE_SCRIPT_DRAFTS_STORAGE_KEY, JSON.stringify(scriptDrafts));
  }, [scriptDrafts]);

  useEffect(() => {
    setSound(defaultSound ?? false);
  }, [defaultSound]);

  useEffect(() => {
    if (!scripts) return;
    setScriptDrafts((prev) => {
      const next = { ...prev };
      for (const rescue of RESCUES) {
        if (!scriptDirty[rescue.kind]) next[rescue.kind] = scripts[rescue.kind] ?? '';
      }
      return next;
    });
  }, [scripts, scriptDirty]);

  const orderedRescues = RESCUES.map((item) => item.kind);
  const topRescue = topPickKind && orderedRescues.includes(topPickKind) ? topPickKind : null;
  const remainingRescues = RESCUES.filter((rescue) => rescue.kind !== topRescue);
  const favoriteSet = new Set(favoriteRescues ?? []);
  const favoriteCards = remainingRescues.filter((rescue) => favoriteSet.has(rescue.kind));
  const otherCards = remainingRescues.filter((rescue) => !favoriteSet.has(rescue.kind));
  const topGuide = topRescue ? rescueGuide(topRescue) : null;

  const promptValueForKind = (kind: RescueKind): string => {
    if (kind === 'make_smaller_10m') return smallerNote;
    if (kind === 'brain_dump_2m' || kind === 'one_sentence') return sentence;
    return '';
  };

  const setPromptValueForKind = (kind: RescueKind, value: string): void => {
    if (kind === 'make_smaller_10m') {
      setSmallerNote(value);
      return;
    }
    if (kind === 'brain_dump_2m' || kind === 'one_sentence') {
      setSentence(value);
    }
  };

  const noteForKind = (kind: RescueKind): string | null => {
    if (kind === 'make_smaller_10m') return smallerNote || null;
    if (kind === 'brain_dump_2m' || kind === 'one_sentence') return sentence || null;
    return null;
  };

  return (
    <div className="stack">
      {onSetInterruption && (
        <details className="card rescue-stage-card rescue-stage-optional">
          <summary>{l.contextStep}</summary>
          <div className="stack rescue-stage-body">
            <div className="small rescue-stage-hint">{l.contextHint}</div>
            {recentContext && onApplyRecentContext && (
              <div className="card rescue-context-card">
                <div className="small">{l.recentContextHint}</div>
                <div className="row-title rescue-context-summary">
                  {[recentContext.subtype ? (subtypeLabel ? subtypeLabel(recentContext.subtype) : frictionSubtypeLabel(recentContext.subtype)) : null, recentContext.interruptionSource ? (interruptionLabel ? interruptionLabel(recentContext.interruptionSource) : recentContext.interruptionSource) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <button className="settings-action" onClick={() => onApplyRecentContext(recentContext)}>
                  {l.applyLastContext}
                </button>
              </div>
            )}
            {onSetSubtype && (
              <>
                <div className="small">{l.whatsHappening}</div>
                <div className="chips">
                  {subtypeOptions.map((item) => (
                    <button key={item.subtype} className={subtype === item.subtype ? 'active' : ''} onClick={() => onSetSubtype(item.subtype)}>
                      {subtypeLabel ? subtypeLabel(item.subtype) : item.label}
                    </button>
                  ))}
                  {hasLegacySelectedSubtype && (
                    <button className="active" disabled>
                      {`${subtypeLabel ? subtypeLabel(subtype) : frictionSubtypeLabel(subtype)} ${l.savedSuffix}`}
                    </button>
                  )}
                  <button className={subtype === null ? 'active' : ''} onClick={() => onSetSubtype(null)}>
                    {l.none}
                  </button>
                </div>
              </>
            )}
            <div className="small">{l.pulledAway}</div>
            <div className="chips">
              {INTERRUPTION_SOURCES.map((item) => (
                <button
                  key={item.source}
                  className={interruptionSource === item.source ? 'active' : ''}
                  onClick={() => onSetInterruption(item.source)}
                >
                  {interruptionLabel ? interruptionLabel(item.source) : item.label}
                </button>
              ))}
              <button className={interruptionSource === null ? 'active' : ''} onClick={() => onSetInterruption(null)}>
                {l.none}
              </button>
            </div>
          </div>
        </details>
      )}
      <div className="card rescue-stage-card">
        <div className="flow-step-label">{l.rescueStep}</div>
        <div className="small rescue-stage-heading">{l.chooseRescue}</div>
        <div className="small rescue-stage-hint">{l.rescueHint}</div>
        {topRescue && (
          <div className="card rescue-row top-pick-rescue" data-kind={topRescue}>
            <div className="stack">
              <div className="small">{l.topPick}</div>
              <div className="row-title rescue-title-with-badge">
                <span className="rescue-mini-badge" data-kind={topRescue}>{rescueBadge(topRescue)}</span>
                <span>{rescueKindLabel ? rescueKindLabel(topRescue) : topRescue}</span>
              </div>
              <div className="small rescue-guide-copy">{scripts?.[topRescue] || topGuide?.summary}</div>
              {topRescue === 'breathe_90s' && topGuide && <BreathingBox labels={topGuide.breathing} />}
            </div>
            <div className="stack rescue-row-actions rescue-row-actions-stack">
              {topRescue === 'make_smaller_10m' && topGuide?.prompt && (
                <input placeholder={topGuide.prompt} value={smallerNote} onChange={(e) => setSmallerNote(e.target.value)} />
              )}
              {topRescue === 'brain_dump_2m' && topGuide?.prompt && (
                <textarea placeholder={topGuide.prompt} value={sentence} maxLength={280} onChange={(e) => setSentence(e.target.value)} />
              )}
              {topRescue === 'one_sentence' && topGuide?.prompt && (
                <textarea placeholder={topGuide.prompt} value={sentence} maxLength={280} onChange={(e) => setSentence(e.target.value)} />
              )}
              <button
                className="start-btn"
                onClick={() =>
                  onStart(
                    topRescue,
                    noteForKind(topRescue),
                    topRescue === 'focus_25m' ? sound : false,
                    prep
                  )
                }
              >
                {topGuide?.actionLabel ?? l.start}
              </button>
            </div>
          </div>
        )}
        {favoriteCards.length > 0 && <div className="small">{l.favorites}</div>}
        <div className="stack rescue-card-list">
          {[...favoriteCards, ...otherCards].map((rescue) => (
            <div key={rescue.kind} className="card rescue-row" data-kind={rescue.kind}>
              <div className="stack">
                <div className="row-title rescue-title-with-badge">
                  <span className="rescue-mini-badge" data-kind={rescue.kind}>{rescueBadge(rescue.kind)}</span>
                  <span>{rescueKindLabel ? rescueKindLabel(rescue.kind) : rescue.label}</span>
                </div>
                <div className="small rescue-guide-copy">{scripts?.[rescue.kind] || rescueGuide(rescue.kind).summary}</div>
                {rescueGuide(rescue.kind).prompt &&
                  (rescue.kind === 'make_smaller_10m' ? (
                    <input
                      placeholder={rescueGuide(rescue.kind).prompt ?? ''}
                      value={promptValueForKind(rescue.kind)}
                      onChange={(e) => setPromptValueForKind(rescue.kind, e.target.value)}
                    />
                  ) : (
                    <textarea
                      placeholder={rescueGuide(rescue.kind).prompt ?? ''}
                      value={promptValueForKind(rescue.kind)}
                      maxLength={280}
                      onChange={(e) => setPromptValueForKind(rescue.kind, e.target.value)}
                    />
                  ))}
              </div>
              <div className="rescue-row-actions">
                {onToggleFavorite && (
                  <button
                    className={`favorite-toggle${favoriteSet.has(rescue.kind) ? ' active' : ''}`}
                    aria-label={favoriteSet.has(rescue.kind) ? l.favoriteOn : l.favoriteOff}
                    onClick={() => onToggleFavorite(rescue.kind)}
                  >
                    {favoriteSet.has(rescue.kind) ? '★' : '☆'}
                  </button>
                )}
                <button
                  className="start-btn"
                  onClick={() => onStart(rescue.kind, noteForKind(rescue.kind), rescue.kind === 'focus_25m' ? sound : false, prep)}
                >
                  {rescueGuide(rescue.kind).actionLabel}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <details className="card rescue-stage-card rescue-stage-optional">
        <summary>{l.supportStep}</summary>
        <div className="stack rescue-stage-body">
          <div className="small rescue-stage-hint">{l.supportHint}</div>
          <div className="small">{l.prepChecklist}</div>
          <div className="card">
            <div className="stack prep-list">
              {RESCUE_PREP_ITEMS.map((item) => (
                <label key={item.key} className="prep-item">
                  <input
                    type="checkbox"
                    checked={prep[item.key]}
                    onChange={(e) => setPrep((prev) => ({ ...prev, [item.key]: e.target.checked }))}
                  />
                  <span>{prepItemLabel ? prepItemLabel(item.key) : item.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="small">{l.resetTip}</div>
          <div className="stack option-stack">
            <input
              placeholder={l.smallestStepPlaceholder}
              value={smallerNote}
              onChange={(e) => setSmallerNote(e.target.value)}
            />
            <button onClick={() => onStart('make_smaller_10m', smallerNote || null, false, prep)}>{l.start10mWithNote}</button>
            <textarea
              placeholder={l.sentencePlaceholder}
              value={sentence}
              maxLength={280}
              onChange={(e) => setSentence(e.target.value)}
            />
            <button onClick={() => onStart('one_sentence', sentence || null, false, prep)}>{l.saveSentence}</button>
            <label>
              <input type="checkbox" checked={sound} onChange={(e) => setSound(e.target.checked)} /> {l.focusSound}
            </label>
          </div>
        </div>
      </details>
      {onSaveScript && (
        <details className="card rescue-stage-card rescue-stage-optional">
          <summary>{l.editRescueText}</summary>
          <div className="stack option-stack">
            {RESCUES.map((rescue) => (
              <div key={rescue.kind} className="stack">
                <div className="small">{rescueKindLabel ? rescueKindLabel(rescue.kind) : rescue.label}</div>
                <textarea
                  value={scriptDrafts[rescue.kind]}
                  maxLength={280}
                  onChange={(e) => {
                    setScriptDrafts((prev) => ({ ...prev, [rescue.kind]: e.target.value }));
                    setScriptDirty((prev) => ({ ...prev, [rescue.kind]: true }));
                  }}
                />
                <button
                  onClick={() => {
                    onSaveScript(rescue.kind, scriptDrafts[rescue.kind]);
                    setScriptDirty((prev) => ({ ...prev, [rescue.kind]: false }));
                  }}
                >
                  {l.saveScript}
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
